import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/stores/authStore";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  Building2, Users, Plus, UserPlus, Trash2, Crown,
  Shield, Eye, Loader2, Mail, FolderKanban
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleIcons = {
  owner: Crown,
  admin: Shield,
  member: Users,
  viewer: Eye
};

const roleColors = {
  owner: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
  admin: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  member: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  viewer: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30"
};

export const OrganizationManager = ({ className = "" }) => {
  const { token, user } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgDetails, setOrgDetails] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", description: "" });
  const [inviteForm, setInviteForm] = useState({ email: "", role: "member" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (token) {
      loadOrganizations();
    }
  }, [token]);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrganizations(response.data);
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error("Failed to load organizations");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadOrgDetails = async (orgId) => {
    try {
      const response = await axios.get(`${API}/organizations/${orgId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrgDetails(response.data);
      setSelectedOrg(orgId);
    } catch (error) {
      toast.error("Failed to load organization details");
    }
  };

  const createOrganization = async () => {
    if (!createForm.name.trim()) {
      toast.error("Organization name is required");
      return;
    }
    
    try {
      setSubmitting(true);
      await axios.post(`${API}/organizations`, createForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Organization created!");
      setShowCreateDialog(false);
      setCreateForm({ name: "", description: "" });
      loadOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create organization");
    } finally {
      setSubmitting(false);
    }
  };

  const inviteMember = async () => {
    if (!inviteForm.email.trim()) {
      toast.error("Email is required");
      return;
    }
    
    try {
      setSubmitting(true);
      await axios.post(`${API}/organizations/${selectedOrg}/invite`, inviteForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invited ${inviteForm.email} as ${inviteForm.role}`);
      setShowInviteDialog(false);
      setInviteForm({ email: "", role: "member" });
      loadOrgDetails(selectedOrg);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to invite member");
    } finally {
      setSubmitting(false);
    }
  };

  const removeMember = async (userId) => {
    if (!confirm("Are you sure you want to remove this member?")) return;
    
    try {
      await axios.delete(`${API}/organizations/${selectedOrg}/members/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Member removed");
      loadOrgDetails(selectedOrg);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove member");
    }
  };

  if (!token) {
    return (
      <Card className={`bg-[#09090b] border-white/5 ${className}`}>
        <CardContent className="p-6 text-center">
          <Building2 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-white font-medium mb-2">Organization Features</h3>
          <p className="text-sm text-zinc-500 mb-4">
            Sign in to create and manage organizations for team collaboration
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className={`bg-[#09090b] border-white/5 ${className}`}>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-[#00f0ff] animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={`bg-[#09090b] border-white/5 ${className}`} data-testid="organization-manager">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#00f0ff]" />
              Organizations
            </CardTitle>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button
                  className="bg-[#00f0ff] text-black hover:bg-[#00f0ff]/90"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#09090b] border-white/10 text-white">
                <DialogHeader>
                  <DialogTitle>Create Organization</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <Label htmlFor="org-name">Organization Name</Label>
                    <Input
                      id="org-name"
                      placeholder="My Team"
                      value={createForm.name}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                      className="bg-black/50 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="org-desc">Description (optional)</Label>
                    <Textarea
                      id="org-desc"
                      placeholder="A brief description of your organization"
                      value={createForm.description}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                      className="bg-black/50 border-white/10 text-white mt-1"
                      rows={3}
                    />
                  </div>
                  <Button
                    onClick={createOrganization}
                    disabled={submitting}
                    className="w-full bg-[#00f0ff] text-black hover:bg-[#00f0ff]/90"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Organization"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {organizations.length === 0 ? (
            <div className="text-center py-8">
              <Building2 className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">No organizations yet</p>
              <p className="text-zinc-600 text-xs mt-1">Create one to start collaborating</p>
            </div>
          ) : (
            <div className="space-y-3">
              {organizations.map((org) => (
                <Card
                  key={org.id}
                  className={`bg-black/30 border-white/5 cursor-pointer transition-all ${
                    selectedOrg === org.id ? "border-[#00f0ff]/50" : "hover:border-white/10"
                  }`}
                  onClick={() => loadOrgDetails(org.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-sm bg-[#00f0ff]/10 border border-[#00f0ff]/20 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-[#00f0ff]" />
                        </div>
                        <div>
                          <h3 className="text-white font-medium">{org.name}</h3>
                          <p className="text-xs text-zinc-500">{org.member_count} members</p>
                        </div>
                      </div>
                      {org.owner_id === user?.id && (
                        <Badge variant="outline" className={roleColors.owner}>
                          <Crown className="w-3 h-3 mr-1" />
                          Owner
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Organization Details Dialog */}
      <Dialog open={!!orgDetails} onOpenChange={() => { setOrgDetails(null); setSelectedOrg(null); }}>
        <DialogContent className="max-w-2xl bg-[#09090b] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-[#00f0ff]" />
              {orgDetails?.name}
            </DialogTitle>
          </DialogHeader>
          
          {orgDetails && (
            <div className="space-y-6 pt-2">
              {orgDetails.description && (
                <p className="text-sm text-zinc-400">{orgDetails.description}</p>
              )}
              
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-white flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#00f0ff]" />
                  Members ({orgDetails.members?.length || 0})
                </h4>
                {(orgDetails.owner_id === user?.id || 
                  orgDetails.members?.some(m => m.user_id === user?.id && ["owner", "admin"].includes(m.role))) && (
                  <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-[#00f0ff] text-black hover:bg-[#00f0ff]/90">
                        <UserPlus className="w-4 h-4 mr-2" />
                        Invite
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#09090b] border-white/10 text-white">
                      <DialogHeader>
                        <DialogTitle>Invite Member</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div>
                          <Label htmlFor="invite-email">Email Address</Label>
                          <Input
                            id="invite-email"
                            type="email"
                            placeholder="colleague@example.com"
                            value={inviteForm.email}
                            onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                            className="bg-black/50 border-white/10 text-white mt-1"
                          />
                        </div>
                        <div>
                          <Label>Role</Label>
                          <Select
                            value={inviteForm.role}
                            onValueChange={(value) => setInviteForm(prev => ({ ...prev, role: value }))}
                          >
                            <SelectTrigger className="bg-black/50 border-white/10 text-white mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-white/10">
                              <SelectItem value="admin">Admin - Can invite/remove members</SelectItem>
                              <SelectItem value="member">Member - Can view & edit projects</SelectItem>
                              <SelectItem value="viewer">Viewer - Can only view projects</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          onClick={inviteMember}
                          disabled={submitting}
                          className="w-full bg-[#00f0ff] text-black hover:bg-[#00f0ff]/90"
                        >
                          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Invite"}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
              
              <div className="space-y-2">
                {orgDetails.members?.map((member) => {
                  const RoleIcon = roleIcons[member.role] || Users;
                  return (
                    <div
                      key={member.user_id}
                      className="flex items-center justify-between p-3 bg-black/30 rounded-sm border border-white/5"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center">
                          <span className="text-xs text-white font-medium">
                            {member.username?.charAt(0).toUpperCase() || "U"}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm text-white">{member.username || member.email}</p>
                          <p className="text-xs text-zinc-500">{member.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={roleColors[member.role]}>
                          <RoleIcon className="w-3 h-3 mr-1" />
                          {member.role}
                        </Badge>
                        {member.role !== "owner" && 
                         (orgDetails.owner_id === user?.id || 
                          orgDetails.members?.some(m => m.user_id === user?.id && ["owner", "admin"].includes(m.role))) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeMember(member.user_id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Organization Projects */}
              <div className="pt-4 border-t border-white/10">
                <Button
                  variant="outline"
                  className="w-full border-white/10 text-white hover:bg-white/5"
                  onClick={async () => {
                    try {
                      const response = await axios.get(`${API}/organizations/${selectedOrg}/projects`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success(`Found ${response.data.length} shared projects`);
                    } catch (error) {
                      toast.error("Failed to load projects");
                    }
                  }}
                >
                  <FolderKanban className="w-4 h-4 mr-2" />
                  View Shared Projects
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
